/**
 * Cloud ROM Service
 * Handles ROM file operations with the backend API
 */

import { apiService } from './api.service';
import type { RomResponseDTO, RomUploadResponse } from '@shared/index';

export interface CloudRom {
  id: number;
  romHash: string;
  romName: string;
  gameCode?: string | null;
  makerCode?: string | null;
  fileSize: number;
  uploadDate: string;
  updatedAt: string;
}

export interface RomUploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

export class CloudRomService {
  /**
   * Upload ROM to cloud storage
   */
  async uploadRom(
    romFile: File,
    romHash: string,
    romName: string,
    gameCode?: string,
    makerCode?: string,
    onProgress?: (progress: RomUploadProgress) => void
  ): Promise<RomUploadResponse> {
    return apiService.uploadRom(
      romFile,
      romHash,
      romName,
      gameCode,
      makerCode,
      (progressEvent) => {
        if (onProgress && progressEvent.total) {
          onProgress({
            loaded: progressEvent.loaded,
            total: progressEvent.total,
            percentage: Math.round((progressEvent.loaded * 100) / progressEvent.total),
          });
        }
      }
    );
  }

  /**
   * Get user's ROM information
   */
  async getUserRom(): Promise<CloudRom | null> {
    const rom = await apiService.getUserRom();
    if (!rom) {
      return null;
    }
    return this.mapToCloudRom(rom);
  }

  /**
   * Download user's ROM file
   */
  async downloadRom(): Promise<Uint8Array> {
    const arrayBuffer = await apiService.downloadRom();
    return new Uint8Array(arrayBuffer);
  }

  /**
   * Delete user's ROM and all associated saves
   */
  async deleteRom(): Promise<void> {
    await apiService.deleteRom();
  }

  /**
   * Check if user has a ROM stored
   */
  async hasRom(): Promise<boolean> {
    const rom = await this.getUserRom();
    return rom !== null;
  }

  /**
   * Calculate ROM hash from file (SHA-256)
   */
  async calculateRomHash(file: File): Promise<string> {
    const arrayBuffer = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Extract ROM metadata from file header
   */
  async extractRomMetadata(file: File): Promise<{
    romName: string;
    gameCode?: string;
    makerCode?: string;
  }> {
    const arrayBuffer = await file.arrayBuffer();
    const header = new Uint8Array(arrayBuffer.slice(0, 512));

    // DS ROM header format:
    // 0x00-0x0B: Game title (12 bytes, ASCII)
    // 0x0C-0x0F: Game code (4 bytes, ASCII)
    // 0x10-0x11: Maker code (2 bytes, ASCII)

    const decoder = new TextDecoder('ascii');

    // Extract game title (trim null bytes and whitespace)
    const titleBytes = header.slice(0, 12);
    const romName = decoder
      .decode(titleBytes)
      .replace(/\0/g, '')
      .trim() || file.name.replace(/\.[^/.]+$/, '');

    // Extract game code
    const gameCodeBytes = header.slice(12, 16);
    const gameCode = decoder
      .decode(gameCodeBytes)
      .replace(/\0/g, '')
      .trim() || undefined;

    // Extract maker code
    const makerCodeBytes = header.slice(16, 18);
    const makerCode = decoder
      .decode(makerCodeBytes)
      .replace(/\0/g, '')
      .trim() || undefined;

    return {
      romName,
      gameCode,
      makerCode,
    };
  }

  /**
   * Validate ROM file
   */
  async validateRomFile(file: File): Promise<{
    valid: boolean;
    error?: string;
  }> {
    // Check file size
    const MIN_ROM_SIZE = 32 * 1024; // 32KB
    const MAX_ROM_SIZE = 512 * 1024 * 1024; // 512MB

    if (file.size < MIN_ROM_SIZE) {
      return {
        valid: false,
        error: 'ROM file too small to be a valid Nintendo DS ROM',
      };
    }

    if (file.size > MAX_ROM_SIZE) {
      return {
        valid: false,
        error: `ROM file too large (max ${MAX_ROM_SIZE / (1024 * 1024)}MB)`,
      };
    }

    // Check file extension
    const validExtensions = ['.nds', '.srl', '.dsi'];
    const hasValidExtension = validExtensions.some((ext) =>
      file.name.toLowerCase().endsWith(ext)
    );

    if (!hasValidExtension) {
      return {
        valid: false,
        error: 'Invalid file type. Only .nds, .srl, or .dsi files are supported',
      };
    }

    return { valid: true };
  }

  /**
   * Map DTO to CloudRom
   */
  private mapToCloudRom(dto: RomResponseDTO): CloudRom {
    return {
      id: dto.id,
      romHash: dto.romHash,
      romName: dto.romName,
      gameCode: dto.gameCode,
      makerCode: dto.makerCode,
      fileSize: dto.fileSize,
      uploadDate: dto.uploadDate,
      updatedAt: dto.updatedAt,
    };
  }

  /**
   * Format file size for display
   */
  formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
}

// Singleton instance
export const cloudRomService = new CloudRomService();
